import { Helmet } from 'react-helmet-async'
import Section from '../components/common/Section'
import eventsData from '../data/events.json'

export default function AusstellungenPage() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const currentEvents = eventsData.filter(e => new Date(e.dateEnd) >= today)
  const pastEvents = eventsData.filter(e => new Date(e.dateEnd) < today)

  return (
    <>
      <Helmet>
        <title>Ausstellungen - Gabriele Wiedemann</title>
        <meta
          name="description"
          content="Aktuelle und vergangene Ausstellungen von Gabriele Wiedemann – zeitgenössische Acrylmalerei."
        />
        <meta property="og:title" content="Ausstellungen - Gabriele Wiedemann" />
        <meta
          property="og:description"
          content="Aktuelle und vergangene Ausstellungen von Gabriele Wiedemann – zeitgenössische Acrylmalerei."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Section background="white" padding="large">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-16 text-center">
            Ausstellungen
          </h1>

          {/* Current exhibitions */}
          <div className="mb-20">
            <h2 className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-8 text-center">
              Aktuelle Ausstellung
            </h2>
            {currentEvents.length > 0 ? (
              <div className="space-y-16">
                {currentEvents.map(event => (
                  <EventCard key={event.id} event={event} featured />
                ))}
              </div>
            ) : (
              <p className="text-center text-neutral-400 text-base py-8 italic">
                Derzeit sind keine Ausstellungen geplant.{' '}
                <br className="hidden sm:block" />
                Besuchen Sie diese Seite wieder, um über kommende Ausstellungen informiert zu bleiben.
              </p>
            )}
          </div>

          {/* Past exhibitions */}
          {pastEvents.length > 0 && (
            <div>
              <h2 className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-8 text-center">
                Vergangene Ausstellungen
              </h2>
              <div className="space-y-20">
                {pastEvents.map(event => (
                  <PastEventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>
    </>
  )
}

function EventCard({ event, featured = false }) {
  return (
    <article
      className={`rounded-xl overflow-hidden shadow-lg ${
        featured ? 'border border-primary/20' : 'border border-neutral-200'
      }`}
    >
      <div className="p-6 md:p-8">
        <h3 className="text-2xl md:text-3xl font-serif text-primary mb-6">
          »{event.title}«
        </h3>
        <ul className="space-y-2 text-sm md:text-base text-neutral-700 mb-6">
          {event.vernissage && (
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✦</span>
              <span>
                <strong className="font-semibold">Vernissage:</strong>{' '}
                {event.vernissage.label}
              </span>
            </li>
          )}
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">✦</span>
            <span>
              <strong className="font-semibold">Ausstellung:</strong> bis{' '}
              {event.dateEndLabel}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">✦</span>
            <span>
              <strong className="font-semibold">Ort:</strong>{' '}
              {event.location.url ? (
                <a
                  href={event.location.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-primary transition-colors duration-200"
                >
                  {event.location.name}
                </a>
              ) : (
                event.location.name
              )}{' '}
              in {event.location.city}
            </span>
          </li>
        </ul>

        {event.description && (
          <p className="text-base text-neutral-600 leading-relaxed">
            {event.description}
          </p>
        )}
      </div>

      {/* Vernissage report */}
      {event.report && (
        <div className="px-6 md:px-8 pb-8 border-t border-neutral-100 pt-8">
          {event.report.title && (
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-8">
              {event.report.title}
            </p>
          )}
          <div className="space-y-8">
            {event.report.sections.map((section, i) =>
              section.type === 'text' ? (
                <p key={i} className="text-base md:text-lg text-neutral-700 leading-relaxed">
                  {section.content}
                </p>
              ) : (
                <figure key={i} className={`my-4 ${section.orientation === 'portrait' ? 'flex flex-col items-center' : ''}`}>
                  <picture>
                    <source srcSet={section.src} type="image/webp" />
                    <img
                      src={section.fallback}
                      alt={section.alt}
                      className={`rounded-xl shadow-md ${
                        section.orientation === 'portrait'
                          ? 'max-h-[640px] w-auto'
                          : 'w-full object-cover max-h-[520px]'
                      }`}
                      loading="lazy"
                    />
                  </picture>
                  {section.alt && (
                    <figcaption className="mt-3 text-xs text-neutral-400 text-center italic">
                      {section.alt}
                    </figcaption>
                  )}
                </figure>
              )
            )}
          </div>
        </div>
      )}
    </article>
  )
}

function PastEventCard({ event }) {
  return (
    <article className="rounded-xl overflow-hidden shadow-lg border border-neutral-200">
      <div className="p-6 md:p-8">
        <h3 className="text-2xl md:text-3xl font-serif text-primary mb-6">
          »{event.title}«
        </h3>

        <ul className="space-y-2 text-sm md:text-base text-neutral-700 mb-6">
          {event.vernissage && (
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">✦</span>
              <span>
                <strong className="font-semibold">Vernissage:</strong>{' '}
                {event.vernissage.label}
              </span>
            </li>
          )}
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">✦</span>
            <span>
              <strong className="font-semibold">Ausstellung:</strong> bis{' '}
              {event.dateEndLabel}
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">✦</span>
            <span>
              <strong className="font-semibold">Ort:</strong>{' '}
              {event.location.url ? (
                <a
                  href={event.location.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-primary transition-colors duration-200"
                >
                  {event.location.name}
                </a>
              ) : (
                event.location.name
              )}{' '}
              in {event.location.city}
            </span>
          </li>
        </ul>

        {event.description && (
          <p className="text-base text-neutral-600 leading-relaxed">
            {event.description}
          </p>
        )}
      </div>

      {event.report && (
        <div className="px-6 md:px-8 pb-8 border-t border-neutral-100 pt-8">
          {event.report.title && (
            <p className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-8">
              {event.report.title}
            </p>
          )}
          <div className="space-y-8">
            {event.report.sections.map((section, i) =>
              section.type === 'text' ? (
                <p key={i} className="text-base md:text-lg text-neutral-700 leading-relaxed">
                  {section.content}
                </p>
              ) : (
                <figure key={i} className={`my-4 ${section.orientation === 'portrait' ? 'flex flex-col items-center' : ''}`}>
                  <picture>
                    <source srcSet={section.src} type="image/webp" />
                    <img
                      src={section.fallback}
                      alt={section.alt}
                      className={`rounded-xl shadow-md ${
                        section.orientation === 'portrait'
                          ? 'max-h-[640px] w-auto'
                          : 'w-full object-cover max-h-[520px]'
                      }`}
                      loading="lazy"
                    />
                  </picture>
                  {section.alt && (
                    <figcaption className="mt-3 text-xs text-neutral-400 text-center italic">
                      {section.alt}
                    </figcaption>
                  )}
                </figure>
              )
            )}
          </div>
        </div>
      )}
    </article>
  )
}
